import { createSlice } from "@reduxjs/toolkit";
import { ScheduleItemDto, timeManagementState } from "./calendar.types";
import {
  createScheduleItem,
  createScheduleItemNote,
  deleteScheduleItem,
  deleteScheduleItemNote,
  getAPICalendarEntries,
  getCalendarItems,
  getDeviceCalendarEntries,
  getRoutines,
  getScheduleItem,
  getScheduleItems,
  updateScheduleItem,
  updateScheduleItemNote,
} from "./scheduleItem.thunk";
import { restoreArchivedTask } from "../tasks/taskThunk";

const initialState: timeManagementState = {
  schedule: [],
  externalCalendarEntries: [],
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
  calendar: [],
  routines: [],
  loadingCache: {
    scheduleItemsLoadedTimeframes: [],
    externalCalendarItemsLoadedTimeframes: [],
    calendarItemsLoadedTimeframes: [],
  },
};

export const calendarSlice = createSlice({
  name: "timeManagement",
  initialState,
  reducers: {
    // Device calendar disconnected in DAEX
    clearDeviceCalendarEntries: (state) => {
      state.externalCalendarEntries = (
        state.externalCalendarEntries || []
      ).filter((entry) => entry.origin !== "device");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getScheduleItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.schedule = action.payload;
      })
      .addCase(getScheduleItems.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getScheduleItems.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(getScheduleItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.calendar = [...state.calendar].map((entry) => {
          if (entry.id === action.payload.id) {
            return action.payload;
          }
          return entry;
        });
      })
      .addCase(getScheduleItem.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getScheduleItem.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(getCalendarItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        const merged = new Map<string, ScheduleItemDto>();

        // Add existing calendar entries first
        state.calendar.forEach((entry) => {
          merged.set(entry.id, entry);
        });

        // Then override or add new ones from the payload
        action.payload.forEach((newEntry) => {
          merged.set(newEntry.id, newEntry);
        });

        state.calendar = Array.from(merged.values());

        const { timeMin, timeMax } = action.meta.arg;
        if (timeMin && timeMax) {
          const existingRanges =
            state.loadingCache.calendarItemsLoadedTimeframes || [];

          const newRange = {
            startDate: timeMin,
            endDate: timeMax,
          };

          const alreadyCached = existingRanges.some(
            (range) =>
              range.startDate === newRange.startDate &&
              range.endDate === newRange.endDate,
          );

          if (!alreadyCached) {
            state.loadingCache.calendarItemsLoadedTimeframes.push(newRange);
          }
        }
      })
      .addCase(getCalendarItems.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getCalendarItems.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(getRoutines.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.routines = action.payload;
      })
      .addCase(getRoutines.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getRoutines.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(getAPICalendarEntries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        const existingIds = new Set(
          (state.externalCalendarEntries || []).map((entry) => entry.id),
        );

        const newEntries = action.payload.items.filter(
          (entry) => !existingIds.has(entry.id),
        );

        state.externalCalendarEntries = [
          ...(state.externalCalendarEntries || []),
          ...newEntries,
        ];

        const newTimeframe = {
          startDate: action.payload.timeMin,
          endDate: action.payload.timeMax,
        };

        const alreadyCached =
          state.loadingCache.externalCalendarItemsLoadedTimeframes.some(
            (range) =>
              range.startDate === newTimeframe.startDate &&
              range.endDate === newTimeframe.endDate,
          );

        if (!alreadyCached) {
          state.loadingCache.externalCalendarItemsLoadedTimeframes.push(
            newTimeframe,
          );
        }
      })
      // Replaces the device events in the range instead of appending, so
      // events deleted or moved on the phone disappear too. No loading or
      // error state: it runs alongside getAPICalendarEntries, and a missing
      // permission is not an error.
      .addCase(getDeviceCalendarEntries.fulfilled, (state, action) => {
        const { items, timeMin, timeMax } = action.payload;
        const min = new Date(timeMin).getTime();
        const max = new Date(timeMax).getTime();
        state.externalCalendarEntries = [
          ...(state.externalCalendarEntries || []).filter(
            (entry) =>
              entry.origin !== "device" ||
              new Date(entry.scheduleEnd).getTime() < min ||
              new Date(entry.scheduleStart).getTime() > max,
          ),
          ...items,
        ];
      })
      // Remembered on request (also before the calendar is connected), so a
      // refresh after connecting knows which range is on screen
      .addCase(getDeviceCalendarEntries.pending, (state, action) => {
        const { timeMin, timeMax } = action.meta.arg;
        const range = state.loadingCache.deviceCalendarRange;
        state.loadingCache.deviceCalendarRange = range
          ? {
              startDate:
                timeMin < range.startDate ? timeMin : range.startDate,
              endDate: timeMax > range.endDate ? timeMax : range.endDate,
            }
          : { startDate: timeMin, endDate: timeMax };
      })
      .addCase(getAPICalendarEntries.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getAPICalendarEntries.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(createScheduleItem.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(createScheduleItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const type = action.payload.type;

        if (type === "taskSchedule") {
          if (state.schedule) {
            state.schedule = [...state.schedule, action.payload];
          } else if (state) {
            state.schedule = [action.payload];
          }
        } else {
          if (state.calendar) {
            state.calendar = [...state.calendar, action.payload];
          } else if (state) {
            state.calendar = [action.payload];
          }
        }

        if (type === "routine") {
          state.routines = [...state.routines, action.payload];
        }
      })
      .addCase(createScheduleItem.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(createScheduleItemNote.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(createScheduleItemNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (action.payload.type === "taskSchedule") {
          state.schedule = [...(state.schedule || []), action.payload];
        }
        if (action.payload.type === "default") {
          if (state.calendar) {
            state.calendar.push(action.payload);
          } else if (state) {
            state.calendar = [action.payload];
          }
        }
      })
      .addCase(createScheduleItemNote.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(updateScheduleItem.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(updateScheduleItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        if (action.payload.type === "taskSchedule" && state.schedule) {
          state.schedule = state.schedule?.map((scheduleItem) =>
            scheduleItem.id !== action.payload.id
              ? scheduleItem
              : action.payload,
          );
        } else if (state.calendar) {
          const isPaused = action.payload.status === "PAUSED";
          const withoutItem = state.calendar.filter(
            (calendarItem) => calendarItem.id !== action.payload.id,
          );
          const isInCalendar = withoutItem.length !== state.calendar.length;

          // The gateway leaves paused items out of getCalendarItems, so mirror
          // that here; a routine that gets resumed has to be added back.
          if (isPaused) {
            state.calendar = withoutItem;
          } else if (isInCalendar) {
            state.calendar = state.calendar.map((calendarItem) =>
              calendarItem.id !== action.payload.id
                ? calendarItem
                : action.payload,
            );
          } else if (action.payload.type === "routine") {
            state.calendar = [...state.calendar, action.payload];
          }
        }

        const routines = state.routines.filter(
          (routine) => routine.id !== action.payload.id,
        );
        if (action.payload.type === "routine") {
          const index = state.routines.findIndex(
            (routine) => routine.id === action.payload.id,
          );
          if (index === -1) {
            routines.push(action.payload);
          } else {
            routines.splice(index, 0, action.payload);
          }
        }
        state.routines = routines;
      })
      .addCase(updateScheduleItem.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })

      .addCase(updateScheduleItemNote.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(updateScheduleItemNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.schedule) {
          state.schedule = state.schedule?.map((scheduleItem) =>
            scheduleItem.id !== action.payload.id
              ? scheduleItem
              : action.payload,
          );
        }
      })
      .addCase(updateScheduleItemNote.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })

      .addCase(deleteScheduleItem.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(deleteScheduleItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        if (state.schedule) {
          state.schedule = state.schedule?.filter(
            (scheduleItem) => scheduleItem.id !== action.payload.id,
          );
        }

        if (state.calendar) {
          state.calendar = state.calendar?.filter(
            (scheduleItem) => scheduleItem.id !== action.payload.id,
          );
        }

        state.routines = state.routines.filter(
          (routine) => routine.id !== action.payload.id,
        );
      })
      .addCase(deleteScheduleItem.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(deleteScheduleItemNote.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(deleteScheduleItemNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.schedule) {
          state.schedule = state.schedule?.filter(
            (scheduleItem) => scheduleItem.id !== action.payload.id,
          );
        }
        if (state.calendar) {
          state.calendar = state.calendar.filter(
            (scheduleItem) => scheduleItem.id !== action.payload.id,
          );
        }
      })
      .addCase(deleteScheduleItemNote.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      // A restored task is a new note: the cached slots still point to its
      // old id (finished this session) or to the archive entry (loaded after
      // a reload). Replace them with the slots the backend moved over.
      .addCase(restoreArchivedTask.fulfilled, (state, action) => {
        const { originalNoteId, NoteScheduling } = action.payload.task;
        const { archivedTaskId } = action.payload;
        state.schedule = [
          ...(state.schedule || []).filter(
            (item) =>
              item.noteId !== originalNoteId &&
              item.archivedNoteId !== archivedTaskId,
          ),
          ...(NoteScheduling || []),
        ];
      });
  },
});

export const { clearDeviceCalendarEntries } = calendarSlice.actions;
export default calendarSlice.reducer;
